import React, { useCallback, useEffect, useState } from 'react';
import Layout from './Layout';
import { Avatar, Button, styled, Typography } from '@mui/material';
import { colors } from '../styles/globalStyles';
import { ReactComponent as ColoredDot } from '../images/colored_dot.svg';
import { ReactComponent as ArrowRight } from '../images/arrow_right.svg';
import { Behavior, BehaviorItems, Buttons, CardsSection, StyledButton, Value } from './progressBehaviors';
import TeammateBehaviorsModal from './teammateBehaviorsModal';
import { getUserBehaviors } from '../api/user';
import { Loader } from './loaders/loader';
import textData from '../data/data.json';
import { selectedLanguageAtom } from '../recoil/atoms/languageAtom';
import { useRecoilValue } from 'recoil';

const UserInfo = styled('div')({
    padding: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 5,
});

const HeadingTypography = styled(Typography)({
    fontWeight: 700,
    fontSize: 24,
    lineHeight: '100%',
    color: colors.main,
});

const Behaviors = styled('div')({
    padding: 17,
    background: colors.transparentMain,
    borderRadius: 40,
    overflowY: 'auto',
});

const BehaviorItem = styled('article')({
    padding: 10,

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',

    background: colors.white,
    borderRadius: 13,

    '&.active': {
        background: colors.main,
    },
});

const TypographyBehaviorItem = styled(Typography)({
    padding: 10,
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '128%',

    '&.active': {
        color: colors.white,
    },
});

const StarsOrRate = styled(Button)({
    width: 52,
    height: 52,

    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',

    background: colors.transparentMain,
    borderRadius: 6,

    '&.active': {
        background: colors.transparentWhite,
    },
    '&:hover': {
        backgroundColor: colors.transparentMain,
    },
});

const TypographyStarsOrRate = styled(Typography)({
    fontWeight: 500,
    fontSize: 16,
    lineHeight: '128%',
    color: colors.main,
});

const TeammateBehaviors = ({ userId }) => {
    const selectedLanguage = useRecoilValue(selectedLanguageAtom);
    const { teammateBehaviors } = textData[selectedLanguage];
    const [progressData, setProgressData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [userData, setUserData] = useState(null);

    const [positive, setPositive] = useState(true);
    const [open, setOpen] = useState(false);
    const [selectedBehavior, setSelectedBehavior] = useState(null);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const { data } = await getUserBehaviors(userId);

            setProgressData(data.values);
            setUserData(data.user);
            setIsLoading(false);
        } catch (err) {
            console.error('Ошибка получения поведений', err);
            setIsLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleRateButtonClick = (behavior, value) => {
        setSelectedBehavior({ ...behavior, value: value });
        setOpen(true);
    };

    return (
        <Layout link={'/my-sessions'} openModal={'search'}>
            <UserInfo>
                <HeadingTypography sx={{ color: 'unset' }}>{teammateBehaviors.title}</HeadingTypography>
                {userData?.avatar ? (
                    <Avatar src={userData.avatar} alt={'avatar'} sx={{ width: 24, height: 24 }} />
                ) : null}
                {userData && userData['first-name'] ? (
                    <HeadingTypography>{userData['first-name']}</HeadingTypography>
                ) : null}
            </UserInfo>
            <Behaviors>
                <Buttons onClick={() => setPositive(!positive)}>
                    <StyledButton className={positive ? 'active' : null}>
                        {teammateBehaviors.sliderButton.mainTitle}
                    </StyledButton>
                    <StyledButton className={positive ? null : 'active'}>
                        {teammateBehaviors.sliderButton.secondaryTitle}
                    </StyledButton>
                </Buttons>
                <CardsSection>
                    {isLoading ? (
                        <Loader size="medium" color={colors.main} hasFullWidth={true} />
                    ) : (
                        progressData &&
                        progressData.length !== 0 &&
                        progressData
                            .filter((value) => value['is_positive'] === positive)
                            .map((value, index) => (
                                <Behavior key={`behavior-${index}`}>
                                    <BehaviorItems>
                                        <Value>
                                            <ColoredDot fill={value['is_positive'] ? colors.green : colors.red} />
                                            {value.value}
                                        </Value>
                                        {value.behaviors.map((behavior, behIndex) => (
                                            <BehaviorItem key={`behavior-${behavior.id}`}>
                                                <TypographyBehaviorItem>{behavior.title}</TypographyBehaviorItem>
                                                <StarsOrRate
                                                    onClick={() => handleRateButtonClick(behavior, value.value)}
                                                >
                                                    {behavior.score ? (
                                                        <TypographyStarsOrRate>
                                                            {behavior.score.result}
                                                        </TypographyStarsOrRate>
                                                    ) : (
                                                        <ArrowRight fill={colors.main} />
                                                    )}
                                                </StarsOrRate>
                                            </BehaviorItem>
                                        ))}
                                    </BehaviorItems>
                                </Behavior>
                            ))
                    )}
                </CardsSection>
            </Behaviors>
            {selectedBehavior !== null && (
                <TeammateBehaviorsModal
                    open={open}
                    onClose={() => {
                        setSelectedBehavior(null);
                        setOpen(false);
                    }}
                    userData={userData}
                    behavior={selectedBehavior}
                    belong={userId}
                    fetchData={fetchData}
                />
            )}
        </Layout>
    );
};

export default TeammateBehaviors;
